import React, { useState } from 'react';
import { AlertTriangle, PhoneCall } from 'lucide-react';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const RED_FLAGS = [
  { key: 'conscience', labelKey: 'triage_red_1' },
  { key: 'respiration', labelKey: 'triage_red_2' },
  { key: 'thorax', labelKey: 'triage_red_3' },
  { key: 'saignement', labelKey: 'triage_red_4' },
  { key: 'convulsions', labelKey: 'triage_red_5' },
  { key: 'paralysie', labelKey: 'triage_red_6' },
  { key: 'allergie', labelKey: 'triage_red_7' },
  { key: 'traumatisme', labelKey: 'triage_red_8' },
];

const ORANGE_FLAGS = [
  { key: 'fievre', labelKey: 'triage_orange_1' },
  { key: 'deshydratation', labelKey: 'triage_orange_2' },
  { key: 'douleur', labelKey: 'triage_orange_3' },
  { key: 'plaie', labelKey: 'triage_orange_4' },
];

const EmergencyTriage = () => {
  const { t } = useLanguage();
  const [red, setRed] = useState({});
  const [orange, setOrange] = useState({});

  const toggleRed = (key) => setRed((c) => ({ ...c, [key]: !c[key] }));
  const toggleOrange = (key) => setOrange((c) => ({ ...c, [key]: !c[key] }));

  const hasRed = Object.values(red).some(Boolean);
  const hasOrange = Object.values(orange).some(Boolean);

  let level = 'vert';
  if (hasRed) level = 'rouge';
  else if (hasOrange) level = 'orange';

  const anyChecked = hasRed || hasOrange;

  return (
    <ToolPageLayout
      title={t('triage_title')}
      subtitle={t('triage_subtitle')}
      specialty="generaliste"
    >
      <div className="bg-[#FEE2E2] border border-[#EF4444]/30 rounded-2xl p-4 mb-8">
        <div className="flex gap-3 mb-3">
          <PhoneCall className="w-5 h-5 text-[#EF4444] shrink-0 mt-0.5" />
          <p className="text-sm text-[#991B1B]">
            {t('triage_call_warning')}
          </p>
        </div>
        <div className="flex gap-3 print:hidden">
          <a
            href="tel:1021"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#EF4444] text-white font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-[#DC2626] transition-colors"
          >
            <PhoneCall className="w-4 h-4" /> {t('triage_civil_protection')}
          </a>
          <a
            href="tel:14"
            className="flex-1 inline-flex items-center justify-center gap-2 bg-[#EF4444] text-white font-semibold text-sm px-4 py-2.5 rounded-xl hover:bg-[#DC2626] transition-colors"
          >
            <PhoneCall className="w-4 h-4" /> {t('triage_samu')}
          </a>
        </div>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3">
            {t('triage_red_question')}
          </h3>
          <div className="space-y-2">
            {RED_FLAGS.map((f) => (
              <label
                key={f.key}
                className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 cursor-pointer hover:border-[#EF4444]/40"
              >
                <input
                  type="checkbox"
                  checked={!!red[f.key]}
                  onChange={() => toggleRed(f.key)}
                  className="w-4 h-4 accent-[#EF4444]"
                />
                <span className="text-sm text-[#0F172A]">{t(f.labelKey)}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h3 className="font-semibold text-[#0F172A] mb-3">
            {t('triage_orange_question')}
          </h3>
          <div className="space-y-2">
            {ORANGE_FLAGS.map((f) => (
              <label
                key={f.key}
                className="flex items-center gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-xl px-4 py-3 cursor-pointer hover:border-[#F59E0B]/40"
              >
                <input
                  type="checkbox"
                  checked={!!orange[f.key]}
                  onChange={() => toggleOrange(f.key)}
                  className="w-4 h-4 accent-[#F59E0B]"
                />
                <span className="text-sm text-[#0F172A]">{t(f.labelKey)}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {anyChecked && (
        <div
          className={`mt-8 rounded-2xl p-6 border flex gap-4 items-start ${
            level === 'rouge'
              ? 'bg-[#FEE2E2] border-[#EF4444]/30'
              : 'bg-[#FEF3C7] border-[#F59E0B]/30'
          }`}
        >
          <AlertTriangle
            className={`w-6 h-6 shrink-0 mt-0.5 ${level === 'rouge' ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`}
          />
          <div>
            <p className={`font-bold ${level === 'rouge' ? 'text-[#991B1B]' : 'text-[#92400E]'}`}>
              {level === 'rouge'
                ? t('triage_result_red_title')
                : t('triage_result_orange_title')}
            </p>
            <p className={`text-sm mt-1 ${level === 'rouge' ? 'text-[#991B1B]' : 'text-[#92400E]'}`}>
              {level === 'rouge'
                ? t('triage_result_red_text')
                : t('triage_result_orange_text')}
            </p>
            {level === 'rouge' && (
              <div className="flex gap-3 mt-4 print:hidden">
                <a
                  href="tel:1021"
                  className="inline-flex items-center gap-2 bg-[#EF4444] text-white font-semibold text-sm px-4 py-2 rounded-xl hover:bg-[#DC2626] transition-colors"
                >
                  <PhoneCall className="w-4 h-4" /> {t('triage_call_1021')}
                </a>
                <a
                  href="tel:14"
                  className="inline-flex items-center gap-2 bg-[#EF4444] text-white font-semibold text-sm px-4 py-2 rounded-xl hover:bg-[#DC2626] transition-colors"
                >
                  <PhoneCall className="w-4 h-4" /> {t('triage_call_14')}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {!anyChecked && (
        <div className="mt-8 rounded-2xl p-6 border bg-[#F0FDF4] border-[#10B981]/30">
          <p className="font-bold text-[#065F46]">{t('triage_none_title')}</p>
          <p className="text-sm text-[#065F46] mt-1">
            {t('triage_none_text')}
          </p>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        {t('triage_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default EmergencyTriage;
